import { inject, observer } from 'mobx-react';
import React from 'react';
import { terminalActions } from '../action';
import { terminalApi } from '../api-layer';
import { Spinner } from '../component/components';
import { Terminal } from '../component/terminal';
import { TabPanel } from '../component/ui';
import { FullTerminal, Stores, TerminalStore } from '../store';

export const TerminalPage: React.SFC<{ path: string }> = props => (
  <div className="terminal-page">
    <TerminalTabs />
  </div>
);

type TerminalTabsOwnProps = {};
type TerminalTabsStoreProps = { terminalStore: TerminalStore };
type TerminalTabsProps = TerminalTabsOwnProps & TerminalTabsStoreProps;
type TermComponentState = {
  activeTerminal?: number;
  terminalToStart?: string;
  lastPath?: string;
};

class TerminalTabsComponent extends React.Component<
  TerminalTabsProps,
  TermComponentState
> {
  terminalElement: HTMLDivElement;
  lastPath: string;

  constructor(props) {
    super(props);
    this.state = {
      activeTerminal: 0,
      terminalToStart: 'cmd.exe',
    };
    this.lastPath = '';
  }

  inRealTerminal() {
    return !!this.props.terminalStore.orderedTerminals[
      this.state.activeTerminal
    ];
  }

  currentTerminal() {
    return this.props.terminalStore.orderedTerminals[this.state.activeTerminal];
  }

  terminalIn(terminal: FullTerminal, terminals: FullTerminal[]) {
    return terminals.some(t => t.descriptor.id === terminal.descriptor.id);
  }

  // setStore = (store: TerminalState) => {
  //   if (this.terminalElement) {
  //     this.terminalElement.scrollTop = this.terminalElement.scrollHeight;
  //   }
  // };

  handleEntry = (terminal: FullTerminal) => {
    const { endOfCommand } = terminal;
    if (endOfCommand) {
      terminalActions.output({
        terminalId: terminal.descriptor.id,
        output:
          (terminal.descriptor.currentPath || this.lastPath) +
          '>' +
          ' ' +
          (terminal.currentCommand || ''),
      });
    }
    terminalApi.input(terminal.currentCommand, terminal.descriptor.id);
    terminalActions.sendCommand({
      terminalId: terminal.descriptor.id,
      endOfCommand,
    });
  };

  handleCommandChange = (terminal: FullTerminal, text: string) =>
    terminalActions.changeCommand(terminal.descriptor.id, text);

  handleBack = (terminal: FullTerminal) =>
    terminalActions.goBack(terminal.descriptor.id);

  handleForward = (terminal: FullTerminal) =>
    terminalActions.goForward(terminal.descriptor.id);

  handleAutocomplete(
    terminal: FullTerminal,
    forward: boolean,
    cursorIndex: number,
  ): void {
    // This allows us to tab through autocomplete results.  Otherwise, autocomplete
    // would evaluate the first autocomplete result on the second Tab press.
    if (!terminal.currentAutocompleteCommand) {
      terminal.currentAutocompleteCommand = terminal.currentCommand;
    }
    terminalApi.autocomplete(
      terminal.currentAutocompleteCommand,
      forward,
      cursorIndex,
      terminal.descriptor.id,
    );
  }

  handleResetAutocomplete(terminal: FullTerminal): void {
    if (terminal.currentAutocompleteCommand) {
      terminalActions.resetAutocomplete(terminal.descriptor.id);
      // Clear autocomplete state on remote computer.
      terminalApi.autocomplete('', true, 0, terminal.descriptor.id);
    }
  }

  getTerminalElement(terminalIndex: number) {
    const { terminalStore } = this.props;

    const orderedTerminals = terminalStore.orderedTerminals;
    const { waitingForNewTerminal } = terminalStore;

    if (orderedTerminals[terminalIndex]) {
      const terminal = orderedTerminals[terminalIndex];
      this.lastPath = terminal.descriptor.currentPath || this.lastPath;

      return (
        <Terminal
          terminal={terminal}
          currentCommand={terminal.currentCommand}
          onCommandChange={text => this.handleCommandChange(terminal, text)}
          onEntry={() => this.handleEntry(terminal)}
          onBack={() => this.handleBack(terminal)}
          onForward={() => this.handleForward(terminal)}
          onAutocomplete={(forward, cursorIndex) =>
            this.handleAutocomplete(terminal, forward, cursorIndex)
          }
          onResetAutocomplete={() => this.handleResetAutocomplete(terminal)}
          autoFocus
        />
      );
    } else {
      if (waitingForNewTerminal) {
        return (
          <div className="new-terminal">
            <Spinner />
          </div>
        );
      } else {
        return (
          <div className="new-terminal">
            <div
              className="create-terminal"
              onClick={() => this.addTerminal(this.state.terminalToStart)}
            >
              Start a terminal
            </div>
            <div
              className="create-terminal-choice"
              onClick={() => this.setState({ terminalToStart: 'cmd.exe' })}
            >
              <input
                type="radio"
                value="cmd.exe"
                checked={this.state.terminalToStart == 'cmd.exe'}
              />
              cmd.exe
            </div>
            <div
              className="create-terminal-choice"
              onClick={() => this.setState({ terminalToStart: 'powershell' })}
            >
              <input
                type="radio"
                value="powershell"
                checked={this.state.terminalToStart == 'powershell'}
              />
              powershell
            </div>
          </div>
        );
      }
    }
  }

  addTerminal(terminalType: string) {
    const orderedTerminals = this.props.terminalStore.orderedTerminals;
    let { activeTerminal } = this.state;
    this.setState({
      activeTerminal: orderedTerminals.length,
    });
    terminalApi.create(terminalType);
  }

  closeTerminal(index: number) {
    const orderedTerminals = this.props.terminalStore.orderedTerminals;
    let { activeTerminal } = this.state;
    if (orderedTerminals[index]) {
      if (activeTerminal >= index && activeTerminal !== 0) {
        this.setState({ activeTerminal: activeTerminal - 1 });
      }
      terminalActions.removeTerminal(orderedTerminals[index].descriptor.id);
      terminalApi.close(orderedTerminals[index].descriptor.id);
    }
  }

  render() {
    let { waitingForNewTerminal } = this.props.terminalStore;
    const orderedTerminals = this.props.terminalStore.orderedTerminals;

    let titles = orderedTerminals.map(
      terminal =>
        terminal.descriptor.terminalType +
        ' - ' +
        (terminal.descriptor.currentPath || ''),
    );

    return (
      <div className="terminal-container">
        <TabPanel
          style={{ height: '100%' }}
          tabs={titles}
          currentTab={this.state.activeTerminal}
          onAdd={terminalType => this.addTerminal(terminalType)}
          onChangeTab={newTab => this.setState({ activeTerminal: newTab })}
          onClose={i => this.closeTerminal(i)}
        />
        {this.getTerminalElement(this.state.activeTerminal)}
      </div>
    );
  }
}

const TerminalTabs = inject<
  Stores,
  TerminalTabsOwnProps,
  TerminalTabsStoreProps,
  {}
>(({ terminalStore }) => ({ terminalStore }))(
  observer(TerminalTabsComponent),
) as React.ComponentType<TerminalTabsOwnProps>;
